import Phaser from 'phaser';

const SKY_TOP = 0x0b0a1f;
const SKY_MID = 0x2e1065;
const SKY_BOT = 0x581c87;

const HEAD_BONE = 0x4b5563;
const HEAD_SHADOW = 0x1f2937;
const HEAD_GLOW = 0x60a5fa;

const STATION_BODY = 0x0f172a;
const NEON_PINK = 0xec4899;
const NEON_CYAN = 0x22d3ee;
const NEON_YELLOW = 0xfde047;
const STAR = 0xffffff;

export function drawKnowhereSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.5) {
      const u = t / 0.5;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.5) / 0.5;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(919);
  g.fillStyle(STAR, 0.8);
  const starCount = Math.max(14, Math.floor(w / 50));
  for (let i = 0; i < starCount; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.65;
    g.fillCircle(sx, sy, rand() < 0.15 ? 2 : 1);
  }

  const nebulaCx = Math.floor(w * 0.3);
  const nebulaCy = Math.floor(h * 0.25);
  g.fillStyle(NEON_PINK, 0.22);
  g.fillCircle(nebulaCx, nebulaCy, Math.min(120, w * 0.14));
  g.fillStyle(NEON_CYAN, 0.15);
  g.fillCircle(nebulaCx + 30, nebulaCy + 20, Math.min(90, w * 0.1));
}

export function drawKnowhereSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  _seed = 919,
) {
  const baselineY = Math.floor(h * 0.8);

  drawCelestialHead(g, w, h, baselineY);
  drawStationSkyline(g, w, baselineY);
}

function drawCelestialHead(
  g: Phaser.GameObjects.Graphics,
  w: number,
  _h: number,
  baselineY: number,
) {
  const headCx = Math.floor(w * 0.65);
  const headCy = baselineY - 30;
  const headW = Math.min(360, w * 0.48);
  const headH = headW * 0.75;

  g.fillStyle(HEAD_BONE, 1);
  g.fillEllipse(headCx, headCy, headW, headH);

  g.fillStyle(HEAD_SHADOW, 0.55);
  g.fillEllipse(headCx - headW * 0.1, headCy + headH * 0.08, headW * 0.85, headH * 0.7);

  const eyeW = headW * 0.16;
  const eyeH = headH * 0.22;
  const eyeY = headCy - headH * 0.08;
  const eyeOffset = headW * 0.16;
  g.fillStyle(HEAD_SHADOW, 1);
  g.fillEllipse(headCx - eyeOffset, eyeY, eyeW, eyeH);
  g.fillEllipse(headCx + eyeOffset, eyeY, eyeW, eyeH);
  g.fillStyle(HEAD_GLOW, 0.75);
  g.fillCircle(headCx - eyeOffset, eyeY, eyeW * 0.28);
  g.fillCircle(headCx + eyeOffset, eyeY, eyeW * 0.28);

  g.fillStyle(HEAD_SHADOW, 0.7);
  const mouthW = headW * 0.38;
  const mouthH = headH * 0.1;
  const mouthY = headCy + headH * 0.18;
  g.fillRoundedRect(headCx - mouthW / 2, mouthY, mouthW, mouthH, 3);

  g.fillStyle(HEAD_BONE, 1);
  const chipW = headW * 0.14;
  g.fillTriangle(
    headCx - headW * 0.45, headCy + headH * 0.25,
    headCx - headW * 0.45 + chipW, headCy + headH * 0.1,
    headCx - headW * 0.45 + chipW * 0.6, headCy + headH * 0.4,
  );
}

function drawStationSkyline(
  g: Phaser.GameObjects.Graphics,
  w: number,
  baselineY: number,
) {
  g.fillStyle(STATION_BODY, 1);
  g.fillRect(0, baselineY, w, 400);

  const buildings: { x: number; w: number; h: number }[] = [];
  let x = 0;
  const seedRand = mulberry32(919);
  while (x < w) {
    const bw = 30 + Math.floor(seedRand() * 40);
    const bh = 22 + Math.floor(seedRand() * 70);
    buildings.push({ x, w: bw, h: bh });
    x += bw + 2;
  }

  for (const b of buildings) {
    const bx = b.x;
    const by = baselineY - b.h;
    g.fillStyle(STATION_BODY, 1);
    g.fillRect(bx, by, b.w, b.h);

    const windowRows = Math.max(1, Math.floor(b.h / 14));
    for (let r = 0; r < windowRows; r++) {
      const wy = by + 4 + r * 14;
      if (wy > baselineY - 4) continue;
      const c = seedRand() < 0.33 ? NEON_PINK : seedRand() < 0.5 ? NEON_CYAN : NEON_YELLOW;
      g.fillStyle(c, 0.8);
      g.fillRect(bx + 3, wy, b.w - 6, 3);
    }
  }

  g.fillStyle(NEON_PINK, 0.8);
  g.fillRect(Math.floor(w * 0.12), baselineY - 70, 24, 28);
  g.fillStyle(NEON_CYAN, 0.8);
  g.fillRect(Math.floor(w * 0.4), baselineY - 90, 28, 40);
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
